<template>
    <div
        :class="['ppv2-flex ppv2-items-center ppv2-space-x-2 ppv2-w-max', customClass, boxed ? boxedClass : '']"
        :style="customStyle"
    >
        <label :for="id" class="ppv2-text-sm ppv2-text-gray-700" v-if="labelLeft">
            {{ labelLeft }}<span v-if="required" class="text-purple">*</span>
        </label>
        <div class="ppv2-relative" :class="disabled ? 'ppv2-cursor-not-allowed' : 'ppv2-cursor-pointer'" @click="handleClick">
            <input type="checkbox" :checked="internalValue" :disabled="disabled" class="ppv2-sr-only" :id="id" readonly />
            <div
                :class="[
                    'ppv2-rounded-full ppv2-transition-all ppv2-ease-in-out ppv2-border-2',
                    sizeClass.wrapper,
                    internalValue ? variantClass.trackActive : variantClass.trackInactive,
                    disabled ? 'ppv2-opacity-50' : '',
                    error ? 'ppv2-border-red-500' : '',
                ]"
            >
                <div
                    :class="[
                        'ppv2-absolute ppv2-top-0.5 ppv2-left-0.5 ppv2-bg-[#fff] ppv2-rounded-full ppv2-shadow-sm ppv2-transform ppv2-transition-all ppv2-ease-in-out',
                        sizeClass.knob,
                        internalValue ? sizeClass.translate : '',
                        disabled ? 'ppv2-bg-gray-100' : '',
                    ]"
                ></div>
            </div>
        </div>

        <label
            :for="id"
            :class="['poppinsLight labelSwitch', customLabelClass]"
            v-if="label"
            :style="[labelStyle, customLabelStyle]"
        >
            {{ label }}<span v-if="required" class="text-purple">*</span>
        </label>
    </div>
</template>

<script>
import normalizeSize from '../../utils/normalizeSize'

export default {
    name: 'BaseSwitch',
    model: {
        prop: 'modelValue',
        event: 'input',
    },
    props: {
        modelValue: {
            type: [Boolean, Array, Number, String, Object],
            default: false,
        },
        value: {
            type: [Boolean, Number, String, Object],
            default: false,
        },
        id: String,
        label: String,
        labelLeft: String,
        disabled: Boolean,
        required: Boolean,
        error: String,
        customClass: String,
        customStyle: [String, Object],
        size: {
            type: String,
            default: 'md',
            validator: (val) => ['sm', 'md', 'lg'].includes(val),
        },
        variant: {
            type: String,
            default: 'purple',
        },
        boxed: Boolean,
        customLabelClass: String,
        customLabelStyle: [String, Object],
    },
    data() {
        return {
            internalValue: false,
        }
    },
    watch: {
        modelValue: {
            handler(val) {
                if (Array.isArray(val)) {
                    this.internalValue = val.includes(this.value)
                } else {
                    this.internalValue = !!val
                }
            },
            immediate: true,
        },
        value() {
            if (Array.isArray(this.modelValue)) {
                this.internalValue = this.modelValue.includes(this.value)
            }
        },
    },
    computed: {
        sizeClass() {
            switch (this.size) {
                case 'sm':
                    return {
                        wrapper: 'ppv2-w-8 ppv2-h-4',
                        knob: 'ppv2-h-3 ppv2-w-3',
                        translate: 'ppv2-translate-x-4',
                    }
                case 'lg':
                    return {
                        wrapper: 'ppv2-w-14 ppv2-h-7',
                        knob: 'ppv2-h-6 ppv2-w-6',
                        translate: 'ppv2-translate-x-7',
                    }
                default:
                    return {
                        wrapper: 'ppv2-w-10 ppv2-h-5',
                        knob: 'ppv2-h-4 ppv2-w-4',
                        translate: 'ppv2-translate-x-5',
                    }
            }
        },
        variantClass() {
            const isSuccess = this.variant === 'success'
            return {
                trackActive: isSuccess ? 'ppv2-bg-[#206A64] ppv2-border-[#206A64]' : 'ppv2-bg-[#7D2DBD] ppv2-border-[#7D2DBD]',
                trackInactive: 'ppv2-bg-black ppv2-bg-opacity-[17%] ppv2-border-black ppv2-border-opacity-[5%]',
                boxed: isSuccess
                    ? this.internalValue
                        ? 'ppv2-bg-[#06B217] ppv2-bg-opacity-20 ppv2-border-[#206A64]'
                        : 'ppv2-border-[#E5E7EB]'
                    : 'ppv2-border-[#E5E7EB]',
            }
        },
        boxedClass() {
            return `ppv2-border ppv2-rounded-[10px] ppv2-px-3 ppv2-py-2 ${this.variantClass.boxed}`
        },
        labelStyle() {
            return { fontSize: this.normalizeSize(15, true) }
        },
    },
    methods: {
        normalizeSize,
        handleClick() {
            if (this.disabled) return
            const newValue = !this.internalValue

            if (Array.isArray(this.modelValue)) {
                let updatedModel = [...this.modelValue]
                if (newValue) {
                    if (!updatedModel.includes(this.value)) {
                        updatedModel.push(this.value)
                    }
                } else {
                    updatedModel = updatedModel.filter((item) => item !== this.value)
                }
                this.$emit('input', updatedModel)
                this.$emit('change', updatedModel)
            } else {
                this.internalValue = newValue
                this.$emit('input', newValue)
                this.$emit('change', newValue)
            }
        },
    },
}
</script>

<style scoped>
.labelSwitch {
    color: #021832;
}
.dark .labelSwitch {
    color: #a0aec0;
}
</style>
